import Loading, { LoadingBall } from '@ingka/loading';
import Text from '@ingka/text';
import {
  useGetLotteryQuery,
  useGetRetailUnitQuery,
} from '@lotta/self-service/lottery-fe/data-access-state';
import {
  useAuthExpiration,
  useErrorToast,
  useLocale,
  useNavigateWithLocale,
} from '@lotta/self-service/lottery-fe/feature-hooks';
import { CopyLotteryForm } from '@lotta/self-service/lottery-fe/feature-lottery-form';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styles from './CopyLottery.module.scss';

/* eslint-disable-next-line */
export interface CopyLotteryProps {}

export const CopyLottery: React.FC<CopyLotteryProps> = () => {
  const isTokenExpired = useAuthExpiration();
  const { lotteryId } = useParams();
  const { retailUnit } = useLocale();
  const navigate = useNavigateWithLocale();
  const { t } = useTranslation();

  const {
    data: retailUnitData,
    isLoading: retailUnitIsLoading,
    error: retailUnitError,
  } = useGetRetailUnitQuery({ retailUnit });
  useErrorToast(retailUnitError);

  const { data, isLoading, error } = useGetLotteryQuery(
    {
      retailUnit,
      lotteryId: lotteryId as string,
    },
    { skip: !lotteryId }
  );

  useErrorToast(error, { 404: 'edit-lottery-getting-error-msg' });

  const handleCancel = useCallback(() => {
    navigate(`/lottery/${lotteryId}`);
  }, [lotteryId, navigate]);

  const handleLotteryCreated = useCallback(
    (_lotteryId: string) => {
      navigate(`/lottery/${_lotteryId}`);
    },
    [navigate]
  );

  if (isTokenExpired) {
    return <Text headingSize="m">{t('lottery-form-auth-expired-text')}</Text>;
  }

  if (retailUnitIsLoading || isLoading) {
    return (
      <Loading text={`${t('general-fetching-lottery')}`}>
        <LoadingBall />
      </Loading>
    );
  }

  if (error || !data || !lotteryId || !retailUnitData) {
    return <Text headingSize="m">{t('general-something-went-wrong')}</Text>;
  }

  const editMode = 'COPY';

  return (
    <div className={styles['container-div']}>
      <h1 className={styles['header']}>{t('edit-lottery-header')}</h1>
      <div className={styles['container']}>
        <div className={styles['form-container']}>
          <CopyLotteryForm
            retailUnit={retailUnit}
            lotteryId={lotteryId}
            editMode={editMode}
            onLotteryCreated={handleLotteryCreated}
            onCancelled={handleCancel}
          />
        </div>
      </div>
    </div>
  );
};
